import React, { useState } from "react";
import { Form, Input, Button, Select, Switch, notification, TextArea } from "lib/ui";
import { UploadOutlined } from "lib/icons";
import dayjs from "dayjs";
import axios from "axios";
import { cn } from "../../../lib/cn";

const { Option } = Select;

const dateFormat = "DD-MM-YYYY";

function SectionCard({ icon, title, description, children, className }) {
  return (
    <section
      className={cn(
        "rounded-2xl border border-violet-100/90 bg-gradient-to-br from-white via-white to-brand-surface/25 p-5 shadow-sm",
        className
      )}
    >
      <div className="mb-4 flex flex-col gap-1 border-b border-violet-100/80 pb-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h3 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-brand-dark">
            <span
              className="flex h-7 w-7 items-center justify-center rounded-lg bg-brand-surface text-xs font-bold text-brand"
              aria-hidden
            >
              {icon}
            </span>
            {title}
          </h3>
          {description ? (
            <p className="mt-1.5 max-w-xl text-xs leading-relaxed text-slate-500">
              {description}
            </p>
          ) : null}
        </div>
      </div>
      <div className="space-y-1">{children}</div>
    </section>
  );
}

const AddCouponForm = ({
  form,
  onFinish,
  initialValues,
  uploadedFile,
  setUploadedFile,
}) => {
  const [loadingImage, setLoadingImage] = useState(false);

  const handleFileChange = async (event) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;

    setLoadingImage(true);
    const img = new Image();
    img.src = URL.createObjectURL(selectedFile);
    img.onload = async () => {
      if (img.width !== 560 || img.height !== 335) {
        notification.error({
          message: "Image size",
          description: "Coupon images must be exactly 560 × 335 pixels.",
        });
        setLoadingImage(false);
        URL.revokeObjectURL(img.src);
        return;
      }

      const formData = new FormData();
      formData.append("image", selectedFile);
      try {
        const response = await axios.post(
          "https://admin.tidyhub.ae/uploadsystem.php",
          formData,
          {
            headers: { "Content-Type": "multipart/form-data" },
            withCredentials: true,
          }
        );
        const imageUrl = response.data.filePath;
        setUploadedFile(imageUrl);
        notification.success({
          message: "Image uploaded",
          description: "Preview updated — remember to save the coupon.",
        });
      } catch (error) {
        console.error("Error uploading file:", error);
        notification.error({
          message: "Upload failed",
          description: "Could not upload the image. Try again.",
        });
      } finally {
        setLoadingImage(false);
        URL.revokeObjectURL(img.src);
      }
    };
  };

  const handleFinish = (values) => {
    const formattedValues = {
      ...values,
      startDate: values.startDate
        ? dayjs(values.startDate).format(dateFormat)
        : null,
      endDate: values.endDate ? dayjs(values.endDate).format(dateFormat) : null,
    };
    onFinish(formattedValues);
  };

  return (
    <div className="text-left">
      <p className="mb-6 text-sm leading-relaxed text-slate-600">
        Use a short <strong className="text-slate-800">coupon code</strong> for
        checkout and optional <strong className="text-slate-800">banner artwork</strong>{" "}
        for the home carousel (560 × 335).
      </p>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        initialValues={initialValues}
        className="flex flex-col gap-5"
      >
        <SectionCard
          icon="1"
          title="Basics"
          description="How customers recognize this promotion."
        >
          <Form.Item
            label="Coupon title"
            name="title"
            rules={[{ required: true, message: "Please enter a title." }]}
          >
            <Input placeholder="e.g. Weekend wash 15% off" />
          </Form.Item>

          <Form.Item
            label="Coupon detail"
            name="detail"
            rules={[{ required: true, message: "Please enter detail text." }]}
          >
            <TextArea
              rows={3}
              placeholder="Short copy shown on the coupon card and in summaries."
              className="resize-y min-h-[88px]"
            />
          </Form.Item>

          <Form.Item
            label="Coupon code"
            name="code"
            rules={[
              { required: true, message: "Enter the code customers use at checkout." },
              {
                pattern: /^[A-Za-z0-9_-]{2,40}$/,
                message: "Use 2–40 letters, numbers, dashes, or underscores.",
              },
            ]}
          >
            <Input placeholder="e.g. WELCOME10" className="font-mono uppercase" />
          </Form.Item>
        </SectionCard>

        <SectionCard
          icon="2"
          title="Creative"
          description="560 × 335 px — matches the in-app coupon banner ratio."
        >
          <Form.Item label={null} className="!mb-0">
            <div
              className={cn(
                "relative overflow-hidden rounded-xl border-2 border-dashed transition-colors",
                uploadedFile
                  ? "border-violet-200 bg-white"
                  : "border-violet-200/70 bg-brand-surface/20 hover:border-brand/40 hover:bg-brand-surface/35"
              )}
            >
              <div className="grid gap-4 p-4 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center">
                <div className="flex min-h-[140px] items-center justify-center rounded-lg bg-slate-50/80 sm:min-h-[160px]">
                  {uploadedFile ? (
                    <img
                      src={uploadedFile}
                      alt="Coupon preview"
                      className="max-h-[200px] w-full max-w-[280px] rounded-lg object-contain shadow-md ring-1 ring-black/5"
                    />
                  ) : (
                    <div className="px-4 text-center">
                      <p className="text-sm font-medium text-slate-700">
                        No artwork yet
                      </p>
                      <p className="mt-1 text-xs text-slate-500">
                        PNG or JPG · exact size 560 × 335
                      </p>
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-2 sm:w-[200px]">
                  <label className="cursor-pointer">
                    <span
                      className={cn(
                        "flex w-full items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold transition",
                        loadingImage
                          ? "cursor-wait bg-slate-100 text-slate-500"
                          : "bg-brand text-white shadow-md shadow-brand/25 hover:brightness-105"
                      )}
                    >
                      <UploadOutlined />
                      {loadingImage ? "Uploading…" : "Upload image"}
                    </span>
                    <input
                      type="file"
                      accept="image/*"
                      className="sr-only"
                      onChange={handleFileChange}
                      disabled={loadingImage}
                    />
                  </label>
                  {uploadedFile ? (
                    <button
                      type="button"
                      className="text-xs font-medium text-red-600 underline-offset-2 hover:underline"
                      onClick={() => setUploadedFile(null)}
                    >
                      Remove image
                    </button>
                  ) : null}
                </div>
              </div>
            </div>
          </Form.Item>
        </SectionCard>

        <SectionCard
          icon="3"
          title="Discount rules"
          description="Fixed locks the discount amount; flexible scales with cart rules if your backend supports it."
        >
          <div className="grid gap-4 md:grid-cols-3">
            <Form.Item
              label="Type"
              name="type"
              className="!mb-0 md:col-span-1"
              rules={[{ required: true, message: "Please select a type." }]}
            >
              <Select placeholder="Select type">
                <Option value="Fixed">Fixed</Option>
                <Option value="Flexible">Flexible</Option>
              </Select>
            </Form.Item>

            <Form.Item
              label="Discount type"
              name="DiscountType"
              className="!mb-0 md:col-span-1"
              rules={[
                { required: true, message: "Please select discount type." },
              ]}
            >
              <Select placeholder="Percentage or fixed AED">
                <Option value="%">Percentage (%)</Option>
                <Option value="AED">Amount (AED)</Option>
              </Select>
            </Form.Item>

            <Form.Item
              label="Unit"
              name="Unit"
              className="!mb-0 md:col-span-1"
              rules={[{ required: true, message: "Please enter unit value." }]}
            >
              <Input placeholder="e.g. 10" />
            </Form.Item>
          </div>
        </SectionCard>

        <SectionCard
          icon="4"
          title="Publishing"
          description="Inactive coupons stay hidden from customers."
          className="border-emerald-100/80 from-white via-white to-emerald-50/30"
        >
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <Form.Item
                label="Status"
                name="status"
                valuePropName="checked"
                className="!mb-0"
              >
                <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
              </Form.Item>
            </div>
            <p className="text-xs text-slate-500 sm:max-w-xs sm:text-right">
              Turn off to draft a coupon before you’re ready to go live.
            </p>
          </div>
        </SectionCard>

        <div className="flex flex-col-reverse gap-3 border-t border-slate-100 pt-5 sm:flex-row sm:justify-end">
          <Button
            type="primary"
            htmlType="submit"
            className="h-11 w-full rounded-xl px-8 text-base font-semibold shadow-lg shadow-brand/20 sm:w-auto"
          >
            Save coupon
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default AddCouponForm;
